import React, { useEffect, useRef, useState } from 'react';

// ── Node type definitions ────────────────────────────────────────────────────
const NT = {
  agency:  { color: '#C8FF00', size: 30, label: 'Agence'         },
  saas:    { color: '#FF1F6A', size: 24, label: 'App SaaS'       },
  site:    { color: '#60A5FA', size: 20, label: 'Site vitrine'   },
  infra:   { color: '#FFCA28', size: 22, label: 'Infrastructure' },
  payment: { color: '#635BFF', size: 16, label: 'Paiement'       },
  tool:    { color: '#34D399', size: 14, label: 'Outil CRM'      },
  db:      { color: '#FB923C', size: 13, label: 'Base données'   },
  client:  { color: '#A78BFA', size: 12, label: 'Client'         },
};

const NODE_ICONS = {
  agency: '🏢', saas: '📱', site: '🌐', infra: '⚙️',
  payment: '💳', tool: '🔧', db: '🗄️', client: '👤',
};

const STATUS_COLOR = {
  en_ligne:    '#4ade80',
  en_dev:      '#fbbf24',
  en_test:     '#60a5fa',
  maintenance: '#a78bfa',
  pause:       '#555',
};

const EDGE_COLORS = {
  'deploy':       '#C8FF00',
  'push → CI/CD': '#C8FF00',
  'API':          '#34D399',
  'sert':         '#34D399',
  'auth':         '#60A5FA',
  'auth+data':    '#60A5FA',
  'data':         '#FB923C',
  'read/write':   '#FB923C',
  'paiement':     '#A78BFA',
  'géocode':      '#FFCA28',
  'héberge':      '#FFCA28',
  'admin':        '#C8FF00',
  'miroir':       '#F472B6',
  'default':      'rgba(255,255,255,0.18)',
};

const EDGE_LEGEND = [
  { label: 'deploy / CI/CD', color: '#C8FF00' },
  { label: 'API',            color: '#34D399' },
  { label: 'auth / auth+data', color: '#60A5FA' },
  { label: 'data / read·write', color: '#FB923C' },
  { label: 'paiement',       color: '#A78BFA' },
  { label: 'héberge',        color: '#FFCA28' },
  { label: 'miroir',         color: '#F472B6' },
];

// ── Static ecosystem graph ───────────────────────────────────────────────────
const BASE_NODES = [
  { id: 'ucreative',    label: 'Ucreative',       type: 'agency',  desc: 'Agence web — Nœud central', url: '' },
  { id: 'firebase',     label: 'Firebase',         type: 'infra',   desc: 'Auth · Hosting · Firestore', url: 'https://firebase.google.com' },
  { id: 'fb_hosting',   label: 'FB Hosting',       type: 'infra',   desc: 'Frontend React — Firebase Hosting', url: 'https://firebase.google.com/products/hosting' },
  { id: 'cloudrun',     label: 'Cloud Run',        type: 'infra',   desc: 'API Express — us-central1', url: 'https://cloud.google.com/run' },
  { id: 'stripe',       label: 'Stripe',           type: 'payment', desc: 'Paiements Vybme · AXISPME', url: 'https://stripe.com' },
  { id: 'vibee',        label: 'Vybme',            type: 'saas',    desc: 'App rencontres authentiques', status: 'en_ligne' },
  { id: 'tacos24',      label: 'TACOS24',          type: 'site',    desc: 'Restaurant vitrine', status: 'en_ligne' },
  { id: 'luthieros',    label: 'LuthierOS',        type: 'saas',    desc: 'SaaS gestion luthiers', status: 'en_ligne' },
  { id: 'axispme',      label: 'AXISPME',          type: 'site',    desc: 'Conseil & accompagnement PME', status: 'en_ligne' },
  { id: 'dashboard',    label: 'Dashboard',        type: 'tool',    desc: 'Admin CRM — local uniquement' },
  { id: 'ucrm',         label: 'ucreative-crm',    type: 'tool',    desc: 'CRM séparé — Phase 1 complète', status: 'en_dev' },
  { id: 'prospection',  label: 'Prospection IA',   type: 'tool',    desc: 'Prospection intelligente ★' },
  { id: 'seo',          label: 'SEO Tools',        type: 'tool',    desc: 'Audit + Analytics + GMB ★' },
  { id: 'facturation',  label: 'Facturation',      type: 'tool',    desc: 'Devis · Factures · Contrats' },
  { id: 'marketing',    label: 'Mail Marketing',   type: 'tool',    desc: 'Campagnes email ★' },
  { id: 'legal',        label: 'Légal IA',         type: 'tool',    desc: 'Génération contrats IA ★' },
  { id: 'tasks',        label: 'Tâches',           type: 'tool',    desc: 'Gestionnaire de tâches' },
  { id: 'fs_profiles',  label: 'vibeeProfiles',    type: 'db',      desc: '~200 profils · Firestore' },
  { id: 'fs_matches',   label: 'vibeeMatches',     type: 'db',      desc: 'Conversations · Firestore' },
  { id: 'fs_clients',   label: 'clients',          type: 'db',      desc: 'CRM clients · Firestore' },
  { id: 'fs_projects',  label: 'projects',         type: 'db',      desc: 'Projets · Firestore' },
  { id: 'fs_swipes',    label: 'vibeeSwipes',      type: 'db',      desc: 'Likes · Firestore' },
  { id: 'nominatim',    label: 'Nominatim',        type: 'infra',   desc: 'Géocodage OpenStreetMap', url: 'https://nominatim.org' },
  { id: 'leaflet',      label: 'Leaflet',          type: 'infra',   desc: 'Cartes interactives Vybme' },
  { id: 'github',       label: 'GitHub',           type: 'infra',   desc: 'Repo ucreativeweb — source', url: 'https://github.com/steve360agence/ucreativeweb' },
  { id: 'gh_actions',   label: 'GH Actions',       type: 'infra',   desc: 'CI/CD — build → push GCR → deploy Cloud Run' },
];

const BASE_EDGES = [
  ['ucreative', 'firebase',    'auth'],
  ['ucreative', 'cloudrun',    'API'],
  ['ucreative', 'dashboard',   'admin'],
  ['ucreative', 'vibee'],
  ['ucreative', 'tacos24'],
  ['ucreative', 'luthieros'],
  ['ucreative', 'axispme'],
  ['vibee',     'firebase',    'auth+data'],
  ['tacos24',   'firebase',    'auth'],
  ['luthieros', 'firebase',    'auth+data'],
  ['axispme',   'firebase',    'auth'],
  ['vibee',     'cloudrun',    'API'],
  ['axispme',   'cloudrun',    'API'],
  ['dashboard', 'cloudrun',    'API'],
  ['ucrm',      'firebase',    'auth+data'],
  ['vibee',     'stripe',      'paiement'],
  ['axispme',   'stripe',      'paiement'],
  ['vibee',     'leaflet'],
  ['vibee',     'nominatim',   'géocode'],
  ['dashboard', 'prospection'],
  ['dashboard', 'seo'],
  ['dashboard', 'facturation'],
  ['dashboard', 'tasks'],
  ['dashboard', 'legal'],
  ['dashboard', 'marketing'],
  ['firebase',  'fs_profiles', 'data'],
  ['firebase',  'fs_matches',  'data'],
  ['firebase',  'fs_swipes',   'data'],
  ['firebase',  'fs_clients',  'data'],
  ['firebase',  'fs_projects', 'data'],
  ['vibee',     'fs_profiles', 'read/write'],
  ['vibee',     'fs_matches',  'read/write'],
  ['vibee',     'fs_swipes',   'read/write'],
  ['dashboard', 'fs_clients',  'read/write'],
  ['dashboard', 'fs_projects', 'read/write'],
  ['firebase',  'fb_hosting',  'héberge'],
  ['fb_hosting','dashboard',   'sert'],
  ['github',    'gh_actions',  'push → CI/CD'],
  ['gh_actions','cloudrun',    'deploy'],
  ['github',    'ucreative'],
  ['ucrm',      'dashboard',   'miroir'],
];

// ── Helpers ──────────────────────────────────────────────────────────────────
function hexAlpha(hex, a) {
  if (!hex || hex.length < 7) return `rgba(136,136,136,${a})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function spherePlace(count) {
  return Array.from({ length: count }, (_, i) => {
    const phi   = Math.acos(-1 + (2 * i) / count);
    const theta = Math.sqrt(count * Math.PI) * phi;
    const r     = 160;
    return {
      x: r * Math.sin(phi) * Math.cos(theta) + (Math.random() - 0.5) * 20,
      y: r * Math.sin(phi) * Math.sin(theta) + (Math.random() - 0.5) * 20,
      z: r * Math.cos(phi) + (Math.random() - 0.5) * 20,
      vx: 0, vy: 0, vz: 0,
    };
  });
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function GraphViewSection({ websites = [], clients = [] }) {
  const containerRef = useRef(null);
  const canvasRef    = useRef(null);
  const rafRef       = useRef(null);
  const stateRef     = useRef(null);
  const [selected,   setSelected]   = useState(null);
  const [nodeCount,  setNodeCount]  = useState(BASE_NODES.length);
  const [edgeCount,  setEdgeCount]  = useState(BASE_EDGES.length);
  const [simRunning, setSimRunning] = useState(true);

  // ── Build / rebuild graph nodes whenever dynamic data changes ──────────────
  useEffect(() => {
    const dynamicNodes = [];
    const dynamicEdges = [];

    // Known site IDs that are already in BASE_NODES — never add as dynamic duplicates
    const KNOWN_IDS = new Set(['vibee', 'tacos24', 'luthieros', 'axispme']);

    websites.forEach(w => {
      const wName  = (w.name  || '').toLowerCase();
      const wDocId = (w.id    || '').toLowerCase();

      // Match by exact label, doc ID, or any significant word in the name
      const words     = wName.split(/[\s\-—_]+/).filter(t => t.length > 3);
      const alreadyIn = BASE_NODES.some(n => {
        const nId    = n.id.toLowerCase();
        const nLabel = n.label.toLowerCase();
        return (
          nLabel === wName ||
          nId    === wDocId ||
          KNOWN_IDS.has(wDocId) ||
          words.some(word => nId.includes(word) || nLabel.includes(word))
        );
      });

      if (!alreadyIn && w.name) {
        const id = `dyn_${wDocId || wName}`;
        dynamicNodes.push({
          id, label: w.name, type: 'site',
          desc: w.client ? `Client : ${w.client}` : 'Site client',
          status: w.status || 'en_dev',
        });
        dynamicEdges.push(['ucreative', id]);
        dynamicEdges.push([id, 'firebase']);
      }
    });

    const allNodeDefs  = [...BASE_NODES, ...dynamicNodes];
    const positions    = spherePlace(allNodeDefs.length);
    const allNodes     = allNodeDefs.map((def, i) => ({ ...def, ...positions[i] }));
    const allEdges     = [...BASE_EDGES, ...dynamicEdges];

    if (!stateRef.current) {
      stateRef.current = {
        nodes: allNodes,
        edges: allEdges,
        rx: -0.25, ry: 0.6,
        zoom: 1,
        drag: null,
        selected: null,
        hovered: null,
        frame: 0,
        simulating: true,
        autoRotate: true,
      };
    } else {
      stateRef.current.nodes    = allNodes;
      stateRef.current.edges    = allEdges;
      stateRef.current.frame    = 0;
      stateRef.current.simulating = true;
    }

    setNodeCount(allNodes.length);
    setEdgeCount(allEdges.length);
  }, [websites.length]);

  // ── Canvas setup + RAF loop ────────────────────────────────────────────────
  useEffect(() => {
    const canvas    = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const dpr = window.devicePixelRatio || 1;
    let resizeRaf = null;

    const resize = (entries) => {
      // Defer to next frame to break the synchronous ResizeObserver loop
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        const rect = entries
          ? entries[0].contentRect
          : container.getBoundingClientRect();
        const w = rect.width;
        const h = rect.height;
        if (!w || !h) return;
        canvas.width  = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width  = w + 'px';
        canvas.style.height = h + 'px';
      });
    };

    resize(null);
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    // ── Physics ──────────────────────────────────────────────────────────────
    function simulate() {
      const s = stateRef.current;
      if (!s || !s.simulating) return;

      const nodes   = s.nodes;
      const REPEL   = 10000;
      const SPRING  = 100;
      const K       = 0.06;
      const DAMP    = 0.84;
      const GRAVITY = 0.018;

      nodes.forEach(n => { n.fx = 0; n.fy = 0; n.fz = 0; });

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 0.1;
          const f    = REPEL / (dist * dist);
          const ux = dx / dist, uy = dy / dist, uz = dz / dist;
          a.fx -= ux * f; a.fy -= uy * f; a.fz -= uz * f;
          b.fx += ux * f; b.fy += uy * f; b.fz += uz * f;
        }
      }

      const nodeMap = {};
      nodes.forEach(n => (nodeMap[n.id] = n));
      s.edges.forEach(([aid, bid]) => {
        const a = nodeMap[aid], b = nodeMap[bid];
        if (!a || !b) return;
        const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 0.1;
        const f    = (dist - SPRING) * K;
        const ux = dx / dist, uy = dy / dist, uz = dz / dist;
        a.fx += ux * f; a.fy += uy * f; a.fz += uz * f;
        b.fx -= ux * f; b.fy -= uy * f; b.fz -= uz * f;
      });

      let maxV = 0;
      nodes.forEach(n => {
        n.fx -= n.x * GRAVITY;
        n.fy -= n.y * GRAVITY;
        n.fz -= n.z * GRAVITY;
        n.vx = (n.vx + n.fx) * DAMP;
        n.vy = (n.vy + n.fy) * DAMP;
        n.vz = (n.vz + n.fz) * DAMP;
        n.x += n.vx; n.y += n.vy; n.z += n.vz;
        maxV = Math.max(maxV, Math.abs(n.vx), Math.abs(n.vy), Math.abs(n.vz));
      });

      s.frame++;
      if (maxV < 0.08 && s.frame > 150) {
        s.simulating = false;
        setSimRunning(false);
      }
    }

    // ── Projection ───────────────────────────────────────────────────────────
    function project(x, y, z, rx, ry, zoom, W, H) {
      const cosY = Math.cos(ry), sinY = Math.sin(ry);
      const x1   = x * cosY + z * sinY;
      const z1   = -x * sinY + z * cosY;
      const cosX = Math.cos(rx), sinX = Math.sin(rx);
      const y2   = y * cosX - z1 * sinX;
      const z2   = y * sinX + z1 * cosX;
      const FOV  = 680;
      const sc   = (FOV * zoom) / (FOV + z2 + 320);
      return { sx: W / 2 + x1 * sc, sy: H / 2 + y2 * sc, z: z2, sc };
    }

    // ── Draw ─────────────────────────────────────────────────────────────────
    function draw() {
      const s = stateRef.current;
      if (!s) return;

      simulate();

      const W   = canvas.width  / dpr;
      const H   = canvas.height / dpr;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Auto-rotate when idle
      if (s.autoRotate && !s.drag && !s.simulating) {
        s.ry += 0.002;
      }

      // Background
      ctx.fillStyle = '#060608';
      ctx.fillRect(0, 0, W, H);

      // Grid
      ctx.strokeStyle = 'rgba(255,255,255,0.05)';
      ctx.lineWidth   = 0.5;
      const gs = 55;
      for (let x = (W / 2) % gs; x < W; x += gs) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      }
      for (let y = (H / 2) % gs; y < H; y += gs) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }

      const { rx, ry, zoom } = s;
      const nodeMap = {};
      s.nodes.forEach(n => (nodeMap[n.id] = n));

      // Project all nodes
      const projected = s.nodes.map(n => ({
        n,
        ...project(n.x, n.y, n.z, rx, ry, zoom, W, H),
      }));
      projected.sort((a, b) => b.z - a.z);

      // ── Edges ─────────────────────────────────────────────────────────────
      const pendingBubbles = [];
      const now = Date.now() / 1000;
      s.edges.forEach(([aid, bid, edgeLabel], edgeIdx) => {
        const a = nodeMap[aid], b = nodeMap[bid];
        if (!a || !b) return;
        const pa    = project(a.x, a.y, a.z, rx, ry, zoom, W, H);
        const pb    = project(b.x, b.y, b.z, rx, ry, zoom, W, H);
        const avgZ  = (pa.z + pb.z) / 2;
        const depth = Math.max(0.12, Math.min(0.55, 0.55 - avgZ * 0.00025));
        const sel   = s.selected && (s.selected === aid || s.selected === bid);
        const hov   = s.hovered  && (s.hovered  === aid || s.hovered  === bid);
        const ecol  = EDGE_COLORS[edgeLabel] || EDGE_COLORS.default;

        // Edge line
        ctx.beginPath();
        ctx.moveTo(pa.sx, pa.sy);
        ctx.lineTo(pb.sx, pb.sy);
        if (sel) {
          ctx.strokeStyle = ecol;
          ctx.lineWidth   = 2.2;
          ctx.shadowBlur  = 10;
          ctx.shadowColor = ecol;
        } else if (hov) {
          ctx.strokeStyle = hexAlpha(ecol, 0.7);
          ctx.lineWidth   = 1.6;
          ctx.shadowBlur  = 0;
        } else if (edgeLabel) {
          ctx.strokeStyle = hexAlpha(ecol, depth * 0.55);
          ctx.lineWidth   = 1.1;
          ctx.shadowBlur  = 0;
        } else {
          ctx.strokeStyle = `rgba(255,255,255,${depth * 0.4})`;
          ctx.lineWidth   = 0.8;
          ctx.shadowBlur  = 0;
        }
        ctx.stroke();
        ctx.shadowBlur = 0;

        // Animated flow dot — only on labeled edges
        if (edgeLabel) {
          const phase   = (edgeIdx * 0.37) % 1;
          const t       = ((now * 0.38 + phase) % 1);
          const dotX    = pa.sx + (pb.sx - pa.sx) * t;
          const dotY    = pa.sy + (pb.sy - pa.sy) * t;
          const dotSize = sel ? 3.5 : hov ? 2.8 : 2;
          const dotA    = sel ? 1 : hov ? 0.8 : depth * 0.7;

          // Glow trail
          const trailLen = 0.12;
          const tStart   = Math.max(0, t - trailLen);
          const trailGrd = ctx.createLinearGradient(
            pa.sx + (pb.sx - pa.sx) * tStart, pa.sy + (pb.sy - pa.sy) * tStart,
            dotX, dotY,
          );
          trailGrd.addColorStop(0, hexAlpha(ecol, 0));
          trailGrd.addColorStop(1, hexAlpha(ecol, dotA * 0.45));
          ctx.beginPath();
          ctx.moveTo(pa.sx + (pb.sx - pa.sx) * tStart, pa.sy + (pb.sy - pa.sy) * tStart);
          ctx.lineTo(dotX, dotY);
          ctx.strokeStyle = trailGrd;
          ctx.lineWidth   = dotSize;
          ctx.stroke();

          // Dot
          ctx.beginPath();
          ctx.arc(dotX, dotY, dotSize, 0, Math.PI * 2);
          ctx.fillStyle   = hexAlpha(ecol, dotA);
          ctx.shadowBlur  = sel ? 12 : 6;
          ctx.shadowColor = ecol;
          ctx.fill();
          ctx.shadowBlur  = 0;
        }

        if (edgeLabel && sel) {
          pendingBubbles.push({ mx: (pa.sx+pb.sx)/2, my: (pa.sy+pb.sy)/2, label: edgeLabel, color: ecol });
        }
      });

      // ── Nodes ─────────────────────────────────────────────────────────────
      const t = Date.now() / 700;
      projected.forEach(({ n, sx, sy, z, sc }) => {
        const type     = NT[n.type] || NT.site;
        const r        = type.size  * sc * 1.25;
        const depthA   = Math.max(0.55, Math.min(1, 1 - z * 0.00035));
        const isSel    = s.selected === n.id;
        const isHov    = s.hovered  === n.id;
        const statCol  = n.status ? (STATUS_COLOR[n.status] || type.color) : type.color;
        const col      = statCol;

        // Glow halo
        const haloR = r * (isSel ? 4.5 : isHov ? 3.5 : 2.8);
        const grd   = ctx.createRadialGradient(sx, sy, 0, sx, sy, haloR);
        grd.addColorStop(0,   hexAlpha(col, isSel ? 0.55 : isHov ? 0.4 : 0.28));
        grd.addColorStop(1,   hexAlpha(col, 0));
        ctx.beginPath();
        ctx.arc(sx, sy, haloR, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();

        // Pulse ring for live/dev status
        if (n.status === 'en_ligne' || n.status === 'en_dev' || n.type === 'saas') {
          const pulse = (Math.sin(t + n.x * 0.01) + 1) / 2;
          ctx.beginPath();
          ctx.arc(sx, sy, r * (1.8 + pulse * 1.1), 0, Math.PI * 2);
          ctx.strokeStyle = hexAlpha(col, 0.3 * pulse * depthA);
          ctx.lineWidth   = 1.5;
          ctx.stroke();
        }

        // Node fill
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fillStyle   = hexAlpha(col, depthA * (isSel ? 1 : 0.92));
        ctx.shadowBlur  = isSel ? 24 : isHov ? 14 : 6;
        ctx.shadowColor = col;
        ctx.fill();
        ctx.shadowBlur  = 0;

        // Border
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.strokeStyle = isSel ? '#fff' : hexAlpha(col, depthA * 0.8);
        ctx.lineWidth   = isSel ? 2.5 : 1.5;
        ctx.stroke();

        // Selection ring
        if (isSel) {
          ctx.beginPath();
          ctx.arc(sx, sy, r + 6, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(200,255,0,0.7)';
          ctx.lineWidth   = 2;
          ctx.stroke();
        }

        // Icon inside node
        if (r > 9) {
          const icon = NODE_ICONS[n.type] || '●';
          const iconSize = Math.max(10, Math.min(22, r * 0.85));
          ctx.font         = `${iconSize}px serif`;
          ctx.textAlign    = 'center';
          ctx.textBaseline = 'middle';
          ctx.globalAlpha  = depthA * 0.9;
          ctx.fillText(icon, sx, sy);
          ctx.globalAlpha  = 1;
        }

        // Label
        const labelFs = Math.max(9, Math.min(13, 11.5 * sc));
        ctx.font         = `${isSel ? 800 : 700} ${labelFs}px -apple-system, "Segoe UI", sans-serif`;
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle    = `rgba(255,255,255,${depthA * (isSel ? 1 : 0.92)})`;
        ctx.shadowBlur   = isSel ? 10 : 4;
        ctx.shadowColor  = col;
        ctx.fillText(n.label, sx, sy + r + 4);
        ctx.shadowBlur   = 0;

        // Desc subtitle under label
        if ((isSel || isHov) && n.desc && sc > 0.5) {
          const descFs = Math.max(7.5, Math.min(10, 9 * sc));
          ctx.font      = `500 ${descFs}px -apple-system, "Segoe UI", sans-serif`;
          ctx.fillStyle = `rgba(255,255,255,${depthA * 0.55})`;
          ctx.fillText(n.desc, sx, sy + r + 4 + labelFs + 3);
        }
      });

      // ── Bulles cartoon — dessinées EN DERNIER, au-dessus de tout ─────────────
      pendingBubbles.forEach(({ mx, my, label, color }) => {
        const col = color || '#C8FF00';
        const fs  = Math.round(Math.max(10, 11 * zoom));
        ctx.font  = `700 ${fs}px -apple-system,sans-serif`;
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        const tw  = ctx.measureText(label).width;
        const pad = 8;
        const bw  = tw + pad * 2;
        const bh  = fs + pad * 1.5;
        const cr  = bh / 2;
        const bx  = mx - bw / 2;
        const by  = my - bh - 10;
        const tip = 7;

        // Ombre externe
        ctx.shadowColor = hexAlpha(col, 0.5);
        ctx.shadowBlur  = 16;

        // Fond bulle
        ctx.beginPath();
        ctx.moveTo(bx + cr, by);
        ctx.lineTo(bx + bw - cr, by);
        ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + cr);
        ctx.lineTo(bx + bw, by + bh - cr);
        ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - cr, by + bh);
        ctx.lineTo(mx + tip, by + bh);
        ctx.lineTo(mx, by + bh + tip * 1.6);
        ctx.lineTo(mx - tip, by + bh);
        ctx.lineTo(bx + cr, by + bh);
        ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - cr);
        ctx.lineTo(bx, by + cr);
        ctx.quadraticCurveTo(bx, by, bx + cr, by);
        ctx.closePath();

        // Remplissage dégradé
        const grd = ctx.createLinearGradient(bx, by, bx, by + bh);
        grd.addColorStop(0, 'rgba(18,18,24,0.97)');
        grd.addColorStop(1, hexAlpha(col, 0.10));
        ctx.fillStyle = grd;
        ctx.fill();
        ctx.shadowBlur  = 0;

        // Bordure colorée
        ctx.beginPath();
        ctx.moveTo(bx + cr, by);
        ctx.lineTo(bx + bw - cr, by);
        ctx.quadraticCurveTo(bx + bw, by, bx + bw, by + cr);
        ctx.lineTo(bx + bw, by + bh - cr);
        ctx.quadraticCurveTo(bx + bw, by + bh, bx + bw - cr, by + bh);
        ctx.lineTo(mx + tip, by + bh);
        ctx.lineTo(mx, by + bh + tip * 1.6);
        ctx.lineTo(mx - tip, by + bh);
        ctx.lineTo(bx + cr, by + bh);
        ctx.quadraticCurveTo(bx, by + bh, bx, by + bh - cr);
        ctx.lineTo(bx, by + cr);
        ctx.quadraticCurveTo(bx, by, bx + cr, by);
        ctx.closePath();
        ctx.strokeStyle = hexAlpha(col, 0.75);
        ctx.lineWidth   = 1.5;
        ctx.stroke();

        // Texte
        ctx.fillStyle = col;
        ctx.fillText(label, mx, by + bh / 2);
      });
    }

    function loop() {
      draw();
      rafRef.current = requestAnimationFrame(loop);
    }

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      ro.disconnect();
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // ── Hit-test helper ─────────────────────────────────────────────────────────
  function getNodeAt(mx, my) {
    const s = stateRef.current;
    if (!s) return null;
    const canvas = canvasRef.current;
    const dpr    = window.devicePixelRatio || 1;
    const W      = canvas.width / dpr;
    const H      = canvas.height / dpr;
    const { rx, ry, zoom } = s;
    const cosY = Math.cos(ry), sinY = Math.sin(ry);
    const cosX = Math.cos(rx), sinX = Math.sin(rx);

    let closest = null, closestD = Infinity;
    s.nodes.forEach(n => {
      const x1 = n.x * cosY + n.z * sinY;
      const z1 = -n.x * sinY + n.z * cosY;
      const y2 = n.y * cosX - z1 * sinX;
      const z2 = n.y * sinX + z1 * cosX;
      const FOV = 680, sc = (FOV * zoom) / (FOV + z2 + 320);
      const sx  = W / 2 + x1 * sc;
      const sy  = H / 2 + y2 * sc;
      const r   = (NT[n.type]?.size || 14) * sc * 1.25;
      const d   = Math.hypot(mx - sx, my - sy);
      if (d < r + 10 && d < closestD) { closestD = d; closest = n; }
    });
    return closest;
  }

  // ── Mouse handlers ──────────────────────────────────────────────────────────
  function onMouseDown(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx   = e.clientX - rect.left;
    const my   = e.clientY - rect.top;
    const s    = stateRef.current;
    s.drag = { sx: mx, sy: my, moved: false, rx0: s.rx, ry0: s.ry };
    s.autoRotate = false;
  }

  function onMouseMove(e) {
    const s    = stateRef.current;
    if (!s) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mx   = e.clientX - rect.left;
    const my   = e.clientY - rect.top;

    if (s.drag) {
      const dx = mx - s.drag.sx, dy = my - s.drag.sy;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) s.drag.moved = true;
      s.ry = s.drag.ry0 + dx * 0.0048;
      s.rx = s.drag.rx0 + dy * 0.0048;
      canvasRef.current.style.cursor = 'grabbing';
    } else {
      const node = getNodeAt(mx, my);
      s.hovered  = node?.id || null;
      canvasRef.current.style.cursor = node ? 'pointer' : 'grab';
    }
  }

  function onMouseUp(e) {
    const s    = stateRef.current;
    if (!s) return;
    if (s.drag && !s.drag.moved) {
      const rect = canvasRef.current.getBoundingClientRect();
      const node = getNodeAt(e.clientX - rect.left, e.clientY - rect.top);
      const newId = node?.id === s.selected ? null : node?.id;
      s.selected = newId;
      setSelected(newId ? node : null);
    }
    s.drag = null;
    canvasRef.current.style.cursor = 'grab';
  }

  function onWheel(e) {
    e.preventDefault();
    const s = stateRef.current;
    if (!s) return;
    s.zoom = Math.max(0.25, Math.min(3.5, s.zoom * (1 - e.deltaY * 0.0009)));
  }

  function onMouseLeave() {
    const s = stateRef.current;
    if (!s) return;
    s.drag    = null;
    s.hovered = null;
  }

  // ── Control helpers ─────────────────────────────────────────────────────────
  const zoomIn  = () => { if (stateRef.current) stateRef.current.zoom = Math.min(3.5, stateRef.current.zoom * 1.22); };
  const zoomOut = () => { if (stateRef.current) stateRef.current.zoom = Math.max(0.25, stateRef.current.zoom * 0.8); };
  const resetCam = () => {
    if (!stateRef.current) return;
    stateRef.current.rx = -0.25;
    stateRef.current.ry = 0.6;
    stateRef.current.zoom = 1;
    stateRef.current.autoRotate = true;
  };
  const reSimulate = () => {
    const s = stateRef.current;
    if (!s) return;
    const positions = spherePlace(s.nodes.length);
    s.nodes.forEach((n, i) => {
      n.x = positions[i].x; n.y = positions[i].y; n.z = positions[i].z;
      n.vx = 0; n.vy = 0; n.vz = 0;
    });
    s.frame = 0;
    s.simulating = true;
    setSimRunning(true);
  };
  const toggleAutoRotate = () => {
    if (stateRef.current) stateRef.current.autoRotate = !stateRef.current.autoRotate;
  };

  // ── Side panel connections list ─────────────────────────────────────────────
  const selectedConnections = selected
    ? (stateRef.current?.edges || [])
        .filter(([a, b]) => a === selected.id || b === selected.id)
        .map(([a, b, lbl]) => {
          const otherId = a === selected.id ? b : a;
          const node = stateRef.current.nodes.find(n => n.id === otherId);
          return node ? { ...node, edgeLabel: lbl, direction: a === selected.id ? 'out' : 'in' } : null;
        })
        .filter(Boolean)
    : [];

  const accent = NT[selected?.type]?.color || '#C8FF00';

  return (
    // position:fixed covers full viewport starting after the 220px sidebar
    <div style={{ position: 'fixed', top: 0, left: 220, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', background: '#060608', zIndex: 90, overflow: 'hidden' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 28px', flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(6,6,8,0.96)', backdropFilter: 'blur(12px)', zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div>
            <div style={{ fontSize: '0.6rem', color: '#333', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Réseau & Écosystème
            </div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 900, color: '#EAEAEA', letterSpacing: '-0.04em', lineHeight: 1.1 }}>
              Graph View <span style={{ color: '#C8FF00', fontSize: '0.5em', fontWeight: 700, letterSpacing: 0 }}>3D</span>
            </h1>
          </div>
          <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.07)' }} />
          <div style={{ fontSize: '0.72rem', color: '#888', fontWeight: 600 }}>
            {nodeCount} nœuds · {edgeCount} liens
          </div>
        </div>
        <div style={{
          padding: '5px 14px', borderRadius: 20, fontSize: '0.68rem', fontWeight: 700,
          background: simRunning ? 'rgba(251,191,36,0.12)' : 'rgba(74,222,128,0.12)',
          color:      simRunning ? '#fbbf24'               : '#4ade80',
          border:     `1px solid ${simRunning ? 'rgba(251,191,36,0.25)' : 'rgba(74,222,128,0.25)'}`,
        }}>
          {simRunning ? '⚡ Simulation…' : '● Stable'}
        </div>
      </div>

      {/* ── Graph + Panel ── */}
      <div style={{ display: 'flex', flex: 1, gap: 0, minHeight: 0 }}>

        {/* Canvas container — full bleed, no border-radius */}
        <div ref={containerRef} style={{
          flex: 1, position: 'relative', overflow: 'hidden',
          background: '#060608', minWidth: 0,
        }}>
          <canvas
            ref={canvasRef}
            style={{ display: 'block', cursor: 'grab' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
            onWheel={onWheel}
          />

          {/* Top-left hint */}
          <div style={{
            position: 'absolute', top: 12, left: 12,
            background: 'rgba(6,6,8,0.75)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 8, padding: '5px 11px', backdropFilter: 'blur(10px)',
            pointerEvents: 'none',
          }}>
            <span style={{ fontSize: '0.6rem', color: '#888', fontWeight: 600 }}>
              Glisser = rotation · Scroll = zoom · Clic = détail
            </span>
          </div>

          {/* Legend bottom-left */}
          <div style={{
            position: 'absolute', bottom: 14, left: 14,
            background: 'rgba(6,6,8,0.88)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 11, padding: '12px 14px', backdropFilter: 'blur(12px)',
            pointerEvents: 'none',
          }}>
            {Object.entries(NT).map(([k, t]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: t.color, boxShadow: `0 0 5px ${t.color}`, flexShrink: 0 }} />
                <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>{t.label}</span>
              </div>
            ))}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 8, paddingTop: 8 }}>
              {Object.entries(STATUS_COLOR).map(([k, c]) => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                  <div style={{ width: 6, height: 6, borderRadius: 2, background: c, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>
                    {k.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 8, paddingTop: 8 }}>
              <div style={{ fontSize: '0.52rem', color: '#333', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>Liens</div>
              {EDGE_LEGEND.map(({ label, color }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                  <div style={{ width: 18, height: 2, borderRadius: 99, background: color, boxShadow: `0 0 4px ${color}`, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Controls bottom-right */}
          <div style={{
            position: 'absolute', bottom: 14, right: 14,
            display: 'flex', flexDirection: 'column', gap: 5,
          }}>
            {[
              { icon: '+',  title: 'Zoom +',          fn: zoomIn },
              { icon: '−',  title: 'Zoom −',          fn: zoomOut },
              { icon: '⟳',  title: 'Reset caméra',    fn: resetCam },
              { icon: '⚡', title: 'Relancer physique',fn: reSimulate },
              { icon: '↺',  title: 'Auto-rotation',   fn: toggleAutoRotate },
            ].map(({ icon, title, fn }) => (
              <button key={icon} onClick={fn} title={title}
                style={{
                  width: 36, height: 36, borderRadius: 9, border: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(6,6,8,0.9)', color: '#666', cursor: 'pointer',
                  fontSize: '1rem', fontWeight: 700, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  backdropFilter: 'blur(8px)', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = '#C8FF00'; e.currentTarget.style.borderColor = 'rgba(200,255,0,0.35)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#666';    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}>
                {icon}
              </button>
            ))}
          </div>
        </div>

        {/* ── Detail panel — slides in from right, flush ── */}
        <div style={{
          width: selected ? 300 : 0,
          overflow: 'hidden',
          transition: 'width 0.28s cubic-bezier(.4,0,.2,1)',
          flexShrink: 0,
          borderLeft: selected ? '1px solid rgba(255,255,255,0.07)' : 'none',
        }}>
          {selected && (
            <div style={{
              width: 300, height: '100%',
              background: '#08080b', padding: '22px 20px', overflowY: 'auto',
              display: 'flex', flexDirection: 'column', gap: 0,
              animation: 'fadeIn 0.2s ease',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
                <span style={{
                  fontSize: '0.6rem', fontWeight: 800, color: accent,
                  textTransform: 'uppercase', letterSpacing: '0.12em',
                  background: hexAlpha(accent, 0.12), padding: '3px 9px', borderRadius: 20,
                }}>
                  {NT[selected.type]?.label || selected.type}
                </span>
                <button onClick={() => { stateRef.current.selected = null; setSelected(null); }}
                  style={{ background: 'none', border: 'none', color: '#333', cursor: 'pointer', fontSize: '1.3rem', lineHeight: 1, padding: 2 }}>×</button>
              </div>

              {/* Hero — icône + nom + type */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                <div style={{
                  width: 54, height: 54, borderRadius: 14, flexShrink: 0,
                  background: `linear-gradient(135deg, ${hexAlpha(accent,0.22)}, ${hexAlpha(accent,0.06)})`,
                  border: `1.5px solid ${hexAlpha(accent, 0.4)}`,
                  boxShadow: `0 0 22px ${hexAlpha(accent, 0.25)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.6rem',
                }}>
                  {NODE_ICONS[selected.type] || '📦'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#EAEAEA', letterSpacing: '-0.03em', lineHeight: 1.2 }}>
                    {selected.label}
                  </div>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 5,
                    padding: '2px 8px', borderRadius: 20,
                    background: hexAlpha(accent, 0.12),
                    border: `1px solid ${hexAlpha(accent, 0.25)}`,
                  }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
                    <span style={{ fontSize: '0.6rem', fontWeight: 800, color: accent, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                      {NT[selected.type]?.label || selected.type}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div style={{
                fontSize: '0.78rem', color: '#888', lineHeight: 1.7, marginBottom: 16,
                padding: '10px 12px', borderRadius: 10,
                background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.05)',
              }}>
                {selected.desc || '—'}
              </div>

              {/* Status + lien */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
                {selected.status && (() => {
                  const sc = STATUS_COLOR[selected.status] || '#555';
                  return (
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '5px 11px', borderRadius: 20,
                      background: hexAlpha(sc, 0.12), border: `1px solid ${hexAlpha(sc, 0.3)}`,
                    }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: sc, boxShadow: `0 0 6px ${sc}`, animation: 'glow 1.8s ease-in-out infinite' }} />
                      <span style={{ fontSize: '0.65rem', fontWeight: 800, color: sc, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {selected.status.replace('_', ' ')}
                      </span>
                    </div>
                  );
                })()}
                {selected.url && (
                  <a href={selected.url} target="_blank" rel="noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '5px 11px', borderRadius: 20,
                      background: hexAlpha(accent, 0.08), border: `1px solid ${hexAlpha(accent, 0.22)}`,
                      color: accent, fontSize: '0.65rem', textDecoration: 'none', fontWeight: 800,
                      letterSpacing: '0.06em', transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = hexAlpha(accent, 0.18); }}
                    onMouseLeave={e => { e.currentTarget.style.background = hexAlpha(accent, 0.08); }}>
                    ↗ Ouvrir
                  </a>
                )}
              </div>

              {/* Stats chips */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 22 }}>
                {[
                  { label: 'Connexions', val: selectedConnections.length, icon: '⬡' },
                  { label: 'Liens sortants', val: selectedConnections.filter(c => c.direction === 'out').length, icon: '→' },
                ].map(({ label, val, icon }) => (
                  <div key={label} style={{
                    background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 10, padding: '11px 13px',
                  }}>
                    <div style={{ fontSize: '0.58rem', color: '#555', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 5 }}>
                      {icon} {label}
                    </div>
                    <div style={{ fontSize: '1.3rem', fontWeight: 900, color: '#EAEAEA', lineHeight: 1 }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Séparateur */}
              <div style={{ fontSize: '0.58rem', fontWeight: 800, color: '#444', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Connexions</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                <span style={{ color: accent }}>{selectedConnections.length}</span>
              </div>

              {/* Connections list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {selectedConnections.map(other => {
                  const oc  = NT[other.type]?.color || '#888';
                  const ec  = EDGE_COLORS[other.edgeLabel] || EDGE_COLORS.default;
                  return (
                    <div key={other.id + other.edgeLabel}
                      onClick={() => { stateRef.current.selected = other.id; setSelected(other); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 10px', borderRadius: 10,
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid rgba(255,255,255,0.05)',
                        cursor: 'pointer', transition: 'all 0.12s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = hexAlpha(oc, 0.25); }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.05)'; }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                        background: hexAlpha(oc, 0.14), border: `1px solid ${hexAlpha(oc, 0.3)}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem',
                      }}>
                        {NODE_ICONS[other.type] || '📦'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#EAEAEA', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {other.label}
                        </div>
                        <div style={{ fontSize: '0.6rem', color: '#555', fontWeight: 600 }}>{NT[other.type]?.label}</div>
                      </div>
                      {other.edgeLabel && (
                        <span style={{
                          fontSize: '0.58rem', fontWeight: 800, padding: '2px 7px', borderRadius: 99,
                          background: hexAlpha(ec, 0.15), border: `1px solid ${hexAlpha(ec, 0.35)}`,
                          color: ec, whiteSpace: 'nowrap', letterSpacing: '0.04em',
                        }}>
                          {other.edgeLabel}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {selectedConnections.length === 0 && (
                <div style={{ fontSize: '0.75rem', color: '#333', textAlign: 'center', padding: '20px 0' }}>
                  Aucune connexion directe
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
